/**
 * Design tokens the MUI theme cannot express per-value, plus the font stacks and the
 * radius/shadow vocabulary. The mechanism is ported from dink-admin (DNKADM-0266/0269/0299);
 * the VALUES are tmm-reseller-admin's neutral genesis palette — re-skin by editing this file and
 * `buildTokens` in ./theme.ts, nothing else. Feature surfaces never read raw literals: they
 * resolve `resolveTokens(useTheme())` / `inkAlpha(theme, α)` (enforced by the ESLint
 * no-raw-color guard over `src/features/**` and `src/routes/**`).
 */

// ---- Brand (neutral genesis: MUI blue) ----
export const BRAND = '#1976D2';
export const BRAND_DARK = '#1565C0';
/** Light page background. */
export const PAGE_BG = '#F5F6F8';

/** Monospace stack — numbers, IDs, keys (JetBrains Mono via @fontsource). */
export const MONO = "'JetBrains Mono', ui-monospace, SFMono-Regular, Menlo, monospace";
/** UI/body stack — Inter (inter-ui, with cv05/cv08 on) — TMM-T1. */
export const SANS = "'Inter', system-ui, Arial, sans-serif";
/** Inter's character variants (TMM-T1): `cv05` tailed l, `cv08` serifed I — keeps I / l / 1 distinct. */
export const INTER_FEATURES = "'cv05' 1, 'cv08' 1";
/** Material Symbols Outlined family name, as the local @font-face declares it. */
export const MATERIAL_SYMBOLS_FONT = "'Material Symbols Outlined Variable'";

/** Corner-radius vocabulary. `theme.shape.borderRadius` = `control` and is ALSO the multiplier
 *  base for numeric `sx.borderRadius` — never change it. */
export const RADII = {
  tag: 4,
  control: 8,
  button: 10,
  row: 12,
  card: 14,
  sheet: 16,
  pill: 999,
} as const;

/** Shell layout: the nav drawer's width and the main column's max width (SHELL-005, TMMRSL-0043). */
export const LAYOUT = { drawerWidth: 256, contentMaxWidth: 1400 } as const;

/**
 * Icon sizes (DS-005, TMMRSL-0073), in px: `Icon`'s `size` prop. `sm` beside small text and in
 * steppers, `md` in menus, `lg` in toolbar buttons, `xl` for a standalone state glyph.
 */
export const ICON_SIZE = { sm: 18, md: 20, lg: 24, xl: 28 } as const;

export type IconSize = keyof typeof ICON_SIZE;

/**
 * Form and filter field widths (FRM-006, TMMRSL-0073), in px: `xs` short codes and numbers, `sm`
 * filter selects, `md` single-line text, `lg` a form's container. Layout widths (`LAYOUT`) and chart
 * geometry are not fields and keep their own values.
 */
export const FIELD_WIDTH = { xs: 160, sm: 200, md: 320, lg: 480 } as const;

export const SHADOWS = {
  card: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
  dialog: '0 11px 15px -7px rgba(0,0,0,0.2), 0 24px 38px 3px rgba(0,0,0,0.14)',
  snackbar: '0 6px 16px rgba(0,0,0,0.3)',
} as const;

/** Two-letter initials from a display name (avatar circles). */
export function initialsOf(name: string): string {
  return name
    .split(' ')
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
}
